import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Header } from './components/Header';
import { StreamList, ChannelAdminActions } from './components/StreamList';
import { StreamPlayer } from './components/StreamPlayer';
import { StatsPanel } from './components/StatsPanel';
import { WatchParty, WatchPartyLauncher } from './components/WatchParty';
import { ChannelChat } from './components/ChannelChat';
import { TimeWindow, SwitchMode, StreamSource, AutoSwitchBehavior, SpamFilterSettings, loadSettings, MAX_CUSTOM_STREAMS } from './components/Settings';
import { getTopStreams, getAuthUrl, getUser, logout, getFollowedStreams, getStreamByLogin, getStreamsByLogin, getUsersByLogin, getGamesByIds, getSharedList, getListStreams as getListRoster, getChannels, createChannel, updateChannel, deleteChannel, TwitchUser, trackConnect, trackDisconnect } from './services/twitchApi';
import { chatMonitor } from './services/chatMonitor';
import { parseGamePath, parseListPath, parseChannelPath, gamePath, canReflectGameInUrl, startupStreamLogin } from './utils/appRoutes';
import { channelStream } from './utils/channelStream';
import { filterNpxlStreams } from './utils/npxlStreams';
import {
  Party,
  PartyStream,
  createParty,
  joinParty,
  leaveParty,
  partyHeartbeat,
  setPartyStream as broadcastPartyStream,
  subscribeParty,
} from './services/watchParty';
import { StreamWithChatRate, GameFilter } from './types';
import {
  Channel,
  CrewChannelId,
  CREW_CHANNEL_IDS,
  ServerChannel,
  DEFAULT_SERVER_CHANNELS,
  cacheServerChannels,
  getChannel,
  isChannelId,
  isCrewChannel,
  loadCachedServerChannels,
  mergeServerChannels,
  normalizeStreamSource,
  resolveCrewRoster,
  rostersFromServer,
} from './constants/channels';
import { getHeatRatio, getHeatTier, isHeatReady } from './utils/heat';
import { filterSwitchPool, pickSwitchTarget } from './utils/autoSwitch';
import { Dismissals } from './utils/dismissals';
import { withMetrics } from './utils/streamMetrics';
import { formatPageTitle } from './utils/pageTitle';
import { notificationPermission, shouldNotify, showSpikeNotification, spikeNotification } from './utils/notify';
import { IconBolt, IconFlame, IconDiscord, IconX } from './components/icons';
import './App.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/** An explicit Dismiss rests a stream this long */
const DISMISS_MS = 5 * 60 * 1000;
/** A popup that timed out unanswered only rests this long, so a still-hot
 *  channel can ask again without nagging every ten seconds */
const SNOOZE_MS = 90 * 1000;

// One channel can live in several pools (top 100, followed, list lookup) —
// keep the first sighting, which is the pool with the richest data
const dedupeByLogin = (pools: StreamWithChatRate[]): StreamWithChatRate[] => {
  const seen = new Set<string>();
  return pools.filter(s => {
    const key = s.user_login.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/** Someone else's list, viewed via /@handle or /s/CODE — never merged into
 *  the visitor's own custom list unless they explicitly save a copy. */
interface SharedListView {
  label: string;
  streams: string[];
}

const App: React.FC = () => {
  const [streams, setStreams] = useState<StreamWithChatRate[]>([]);
  const [followedStreams, setFollowedStreams] = useState<StreamWithChatRate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<TwitchUser | null>(null);
  const [session, setSession] = useState<string | null>(() => localStorage.getItem('twitch_session'));
  const [sharedList, setSharedList] = useState<SharedListView | null>(null);
  const [sharedListLoading, setSharedListLoading] = useState(() => parseListPath(window.location.pathname) !== null);

  // Settings state - load from localStorage
  const savedSettings = loadSettings();
  const [timeWindow, setTimeWindow] = useState<TimeWindow>(savedSettings?.timeWindow || '5s');
  const [switchMode, setSwitchMode] = useState<SwitchMode>(savedSettings?.switchMode || 'threshold');
  const [threshold, setThreshold] = useState(savedSettings?.threshold || 2.5);
  const [languageFilter, setLanguageFilter] = useState(savedSettings?.languageFilter || 'en');
  const [cooldown, setCooldown] = useState(savedSettings?.cooldown || 40);
  const [minChatRate, setMinChatRate] = useState(savedSettings?.minChatRate || 2.5);
  const [gameBlacklist, setGameBlacklist] = useState<string[]>(savedSettings?.gameBlacklist || []);
  const [streamBlacklist, setStreamBlacklist] = useState<string[]>(savedSettings?.streamBlacklist || []);
  const [favoriteStream, setFavoriteStream] = useState(savedSettings?.favoriteStream || '');
  // The dial's catalogue: the last server catalogue this browser saw (so a
  // reload racks created channels straight away), the constants until then
  const [channels, setChannels] = useState<Channel[]>(() => {
    const cached = loadCachedServerChannels();
    return mergeServerChannels(cached && cached.length > 0 ? cached : DEFAULT_SERVER_CHANNELS);
  });
  const channelsRef = useRef(channels);
  useEffect(() => {
    channelsRef.current = channels;
  }, [channels]);
  const [selectedStream, setSelectedStream] = useState<StreamWithChatRate | null>(() => {
    const login = startupStreamLogin(window.location.pathname, channels);
    return login ? channelStream(login) : null;
  });
  // The active channel — older builds saved 'recommended' + a recList key
  const [streamSource, setStreamSource] = useState<StreamSource>(() => {
    const channelRoute = parseChannelPath(window.location.pathname, channels);
    if (channelRoute) return channelRoute.channelId;
    if (window.location.pathname === '/') return 'npxl';
    return normalizeStreamSource(savedSettings?.streamSource, savedSettings?.recList, channels);
  });
  const [customStreams, setCustomStreams] = useState<string[]>(savedSettings?.customStreams || []);
  const [autoSwitchBehavior, setAutoSwitchBehavior] = useState<AutoSwitchBehavior>(savedSettings?.autoSwitchBehavior || 'auto');
  const [spamFilter, setSpamFilter] = useState<SpamFilterSettings>(savedSettings?.spamFilter || { filterVotes: true, filterCommands: true });
  const [notifyWhenHidden, setNotifyWhenHidden] = useState<boolean>(savedSettings?.notifyWhenHidden === true);
  // Category lock for the Top 100 — a /g/ deep link beats the saved setting
  const [gameFilter, setGameFilter] = useState<GameFilter | null>(() => {
    const fromUrl = parseGamePath(window.location.pathname);
    if (fromUrl) {
      const saved: GameFilter | null = savedSettings?.gameFilter || null;
      return saved?.id === fromUrl.gameId ? saved : { id: fromUrl.gameId, name: '' };
    }
    return savedSettings?.gameFilter || null;
  });
  const [cooldownProgress, setCooldownProgress] = useState(0);
  const [sidebarHidden, setSidebarHidden] = useState(() => window.innerWidth <= 768);
  const [showGuide, setShowGuide] = useState(() => !localStorage.getItem('guideShown'));
  const [liveCount, setLiveCount] = useState<number | null>(null);
  const [chatConnected, setChatConnected] = useState(true);
  // The active channel's shared room under the Twitch chat — hide it and it
  // stays hidden across channels and reloads
  const [channelChatCollapsed, setChannelChatCollapsed] = useState(
    () => localStorage.getItem('pogchamp_channel_chat_collapsed') === '1',
  );
  const toggleChannelChat = useCallback(() => {
    setChannelChatCollapsed(prev => {
      localStorage.setItem('pogchamp_channel_chat_collapsed', prev ? '0' : '1');
      return !prev;
    });
  }, []);

  // Popup switch suggestion state
  const [suggestedStream, setSuggestedStream] = useState<StreamWithChatRate | null>(null);
  const [popupTimeLeft, setPopupTimeLeft] = useState(0);
  const popupTimerRef = useRef<NodeJS.Timeout | null>(null);
  const suggestedStreamRef = useRef<StreamWithChatRate | null>(null);

  useEffect(() => {
    suggestedStreamRef.current = suggestedStream;
  }, [suggestedStream]);

  // Streams the viewer waved off (see DISMISS_MS / SNOOZE_MS)
  const dismissedStreamsRef = useRef(new Dismissals());
  // Last alert per stream, so a channel that keeps spiking only pings once
  const lastAlertRef = useRef<Map<string, number>>(new Map());

  // Last switch timestamp for cooldown (initialize to current time minus cooldown)
  const lastSwitchTime = useRef<number>(selectedStream
    ? Date.now()
    : Date.now() - (savedSettings?.cooldown || 30) * 1000);

  // Track selected stream for favorite return logic
  const selectedStreamRef = useRef<StreamWithChatRate | null>(selectedStream);

  // Track followed streams for auto-switch
  const followedStreamsRef = useRef<StreamWithChatRate[]>([]);
  // Snapshot of latest streams for use in callbacks that need to avoid re-subscribing
  const streamsRef = useRef<StreamWithChatRate[]>([]);

  // Custom-list + rec-crew channels resolved straight from Helix, so lists
  // work even when their channels are nowhere near the top 100
  const [listStreams, setListStreams] = useState<StreamWithChatRate[]>([]);
  const [listUsers, setListUsers] = useState<Record<string, TwitchUser>>({});
  const listStreamsRef = useRef<StreamWithChatRate[]>([]);

  // Crew rosters from the server's custom-lists.json — the curated constants
  // only cover for a crew the server doesn't know or a failed fetch
  const [crewRosters, setCrewRosters] = useState<Partial<Record<CrewChannelId, string[]>>>(() => {
    const cached = loadCachedServerChannels();
    return rostersFromServer(cached && cached.length > 0 ? cached : DEFAULT_SERVER_CHANNELS);
  });
  const crewRostersRef = useRef(crewRosters);
  useEffect(() => {
    crewRostersRef.current = crewRosters;
  }, [crewRosters]);

  // The server's catalogue is the source of truth for roster channels: the
  // built-ins plus every channel a channel admin created, rosters included.
  // A source that no longer exists (a deleted channel) falls back to Top 100.
  const applyServerChannels = useCallback((server: ServerChannel[]) => {
    const list = Array.isArray(server) && server.some(s => s.id === 'npxl')
      ? server
      : [...(server || []), ...DEFAULT_SERVER_CHANNELS];
    const merged = mergeServerChannels(list);
    setChannels(merged);
    setCrewRosters(rostersFromServer(list));
    cacheServerChannels(list);
    setStreamSource(prev => (isChannelId(prev, merged) ? prev : 'top'));
  }, []);
  useEffect(() => {
    getChannels().then(server => {
      if (server) {
        applyServerChannels(server);
        return;
      }
      // Unreachable (or an older API) — the constants stand, rosters per handle
      CREW_CHANNEL_IDS.forEach(id => {
        const handle = getChannel(id).handle;
        if (!handle) return;
        getListRoster(handle).then(streams => {
          if (streams && streams.length > 0) {
            setCrewRosters(prev => ({ ...prev, [id]: streams }));
          }
        });
      });
    });
  }, [applyServerChannels]);

  // Channel admins edit the dial itself: every change lands on the server
  // and the catalogue it hands back replaces ours
  const channelAdmin = useMemo<ChannelAdminActions | null>(() => {
    if (!user?.channelAdmin || !session) return null;
    return {
      saveRoster: async (id, streams) => {
        const result = await updateChannel(session, id, { streams });
        applyServerChannels(result.channels);
      },
      createChannel: async values => {
        const result = await createChannel(session, { label: values.label, tag: values.tag });
        applyServerChannels(result.channels);
        return result.channel?.id ?? 'top';
      },
      updateChannel: async (id, values) => {
        const result = await updateChannel(session, id, { label: values.label, tag: values.tag });
        applyServerChannels(result.channels);
      },
      deleteChannel: async id => {
        const result = await deleteChannel(session, id);
        applyServerChannels(result.channels);
      },
    };
  }, [user?.channelAdmin, session, applyServerChannels]);

  // While a shared list is open it stands in for the visitor's custom list
  // everywhere (sidebar, pools, monitoring) without overwriting it
  const sharedListRef = useRef<SharedListView | null>(null);
  useEffect(() => {
    sharedListRef.current = sharedList;
  }, [sharedList]);
  const activeCustomStreams = useMemo(
    () => (sharedList ? sharedList.streams : customStreams),
    [sharedList, customStreams]
  );

  // Watch party state
  const [party, setParty] = useState<Party | null>(null);
  const [partyParticipantId, setPartyParticipantId] = useState<string | null>(null);
  const [partyIsHost, setPartyIsHost] = useState(false);
  const [showPartyLauncher, setShowPartyLauncher] = useState(false);
  const [partyBusy, setPartyBusy] = useState(false);
  const [partyError, setPartyError] = useState<string | null>(null);
  const [partyPanelOpen, setPartyPanelOpen] = useState(true);
  const [pendingPartyCode, setPendingPartyCode] = useState<string | null>(null);
  const partyRef = useRef<{ code: string; participantId: string; isHost: boolean } | null>(null);
  const lastBroadcastStreamIdRef = useRef<string | null>(null);

  useEffect(() => {
    partyRef.current = party && partyParticipantId
      ? { code: party.code, participantId: partyParticipantId, isHost: partyIsHost }
      : null;
  }, [party, partyParticipantId, partyIsHost]);

  const broadcastSelectedStreamToParty = useCallback((stream: StreamWithChatRate) => {
    const current = partyRef.current;
    if (!current || !current.isHost) return;
    if (lastBroadcastStreamIdRef.current === stream.id) return;

    lastBroadcastStreamIdRef.current = stream.id;
    broadcastPartyStream(current.code, current.participantId, stream).catch(err => {
      lastBroadcastStreamIdRef.current = null;
      console.error('Failed to broadcast party stream', err);
    });
  }, []);


  useEffect(() => {
    selectedStreamRef.current = selectedStream;
  }, [selectedStream]);

  // Track visitor connection with heartbeat
  useEffect(() => {
    let visitorId = localStorage.getItem('visitor_id');

    trackConnect(visitorId || undefined, selectedStreamRef.current?.user_login).then(result => {
      if (result.visitorId && !visitorId) {
        localStorage.setItem('visitor_id', result.visitorId);
        visitorId = result.visitorId;
      }
      if (result.active > 0) setLiveCount(result.active);
    });

    // Heartbeat every 15 seconds to keep connection alive
    const heartbeat = setInterval(() => {
      if (visitorId) {
        trackConnect(visitorId, selectedStreamRef.current?.user_login).then(result => {
          if (result.active > 0) setLiveCount(result.active);
        });
      }
    }, 15000);

    // Track disconnect on page unload
    const handleUnload = () => {
      if (visitorId) {
        const blob = new Blob([JSON.stringify({ visitorId })], { type: 'application/json' });
        navigator.sendBeacon(`${API_BASE}/api/disconnect`, blob);
      }
    };

    window.addEventListener('beforeunload', handleUnload);
    return () => {
      clearInterval(heartbeat);
      window.removeEventListener('beforeunload', handleUnload);
      if (visitorId) {
        trackDisconnect(visitorId);
      }
    };
  }, []);

  useEffect(() => {
    followedStreamsRef.current = followedStreams;
  }, [followedStreams]);

  // Reflect IRC connection health in the UI
  useEffect(() => {
    chatMonitor.onStatusChange(setChatConnected);
  }, []);

  useEffect(() => {
    streamsRef.current = streams;
  }, [streams]);


  // Use refs for settings to avoid recreating callback
  const settingsRef = useRef({
    timeWindow,
    switchMode,
    threshold,
    languageFilter,
    cooldown,
    minChatRate,
    gameBlacklist,
    streamBlacklist,
    favoriteStream,
    streamSource,
    customStreams,
    autoSwitchBehavior,
    gameFilter,
    notifyWhenHidden,
  });

  useEffect(() => {
    settingsRef.current = {
      timeWindow,
      switchMode,
      threshold,
      languageFilter,
      cooldown,
      minChatRate,
      gameBlacklist,
      streamBlacklist,
      favoriteStream,
      streamSource,
      customStreams,
      autoSwitchBehavior,
      gameFilter,
      notifyWhenHidden,
    };
  }, [timeWindow, switchMode, threshold, languageFilter, cooldown, minChatRate, gameBlacklist, streamBlacklist, favoriteStream, streamSource, customStreams, autoSwitchBehavior, gameFilter, notifyWhenHidden]);

  // The custom list that's actually in play (yours, or the shared one) —
  // callbacks read through these instead of settingsRef so a shared view
  // swaps in cleanly
  const activeCustomStreamsFor = useCallback((): string[] => {
    const shared = sharedListRef.current;
    return shared ? shared.streams : settingsRef.current.customStreams;
  }, []);

  const activeStreamSourceFor = useCallback((): StreamSource => {
    return sharedListRef.current ? 'custom' : settingsRef.current.streamSource;
  }, []);

  // Handle OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionParam = params.get('session');
    if (sessionParam) {
      setSession(sessionParam);
      localStorage.setItem('twitch_session', sessionParam);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Handle /party/:code routes - open launcher pre-filled with code
  useEffect(() => {
    const path = window.location.pathname;
    const match = path.match(/^\/party\/([A-Za-z0-9]{4,10})\/?$/);
    if (match) {
      setPendingPartyCode(match[1].toUpperCase());
      setShowPartyLauncher(true);
    }
  }, []);

  // Handle /@handle and /s/CODE shared list routes — load before anything
  // else, into a view of its own (the visitor's saved list stays untouched)
  useEffect(() => {
    const route = parseListPath(window.location.pathname);
    if (!route) return;

    const finish = () => setSharedListLoading(false);
    if (route.kind === 'handle') {
      fetch(`${API_BASE}/api/list/${route.handle}`)
        .then(res => res.json())
        .then(data => {
          if (Array.isArray(data.streams) && data.streams.length > 0) {
            setSharedList({ label: `@${route.handle}`, streams: data.streams });
          }
        })
        .catch(() => {})
        .finally(finish);
    } else {
      getSharedList(route.code)
        .then(streams => {
          if (streams && streams.length > 0) {
            setSharedList({ label: 'Shared list', streams });
          }
        })
        .finally(finish);
    }
  }, []);

  // Resolve a /g/ deep link's id to its name + box art. An id Twitch doesn't
  // know falls back to the unfiltered top 100.
  useEffect(() => {
    if (!gameFilter || gameFilter.name) return;
    let cancelled = false;
    getGamesByIds([gameFilter.id]).then(games => {
      if (cancelled) return;
      const game = games[0];
      if (game) {
        setGameFilter({ id: game.id, name: game.name, boxArt: game.box_art_url });
      } else {
        setGameFilter(null);
      }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameFilter?.id]);

  // Keep the URL shareable: the armed category lives at /g/<id>. Party and
  // shared-list URLs are never rewritten.
  useEffect(() => {
    const path = window.location.pathname;
    if (!canReflectGameInUrl(path)) return;
    const next = gameFilter ? gamePath(gameFilter.id) : '/';
    if (path !== next) {
      window.history.replaceState({}, '', next + window.location.search);
    }
  }, [gameFilter]);

  // Load user on mount if session exists
  useEffect(() => {
    if (session) {
      getUser(session).then(u => {
        if (u) {
          setUser(u);
        } else {
          // Invalid session
          setSession(null);
          localStorage.removeItem('twitch_session');
        }
      });
    }
  }, [session]);

  // Fetch followed streams when user is logged in
  const fetchFollowedStreams = useCallback(async () => {
    if (!session) return;
    const followed = await getFollowedStreams(session);
    const seen = new Set<string>();
    const deduped = followed.filter(stream => {
      const key = stream.user_login.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    const followedWithChatRate: StreamWithChatRate[] = deduped.map(stream => ({
      ...stream,
      chatRate: 0,
      messageCount: 0,
    }));
    // Also monitor followed streams chat (in parallel) - mark as priority
    await Promise.all(deduped.map(stream => chatMonitor.startMonitoring(stream.user_login, true)));
    setFollowedStreams(followedWithChatRate);
  }, [session]);

  useEffect(() => {
    if (user && session) {
      fetchFollowedStreams();
      // Refresh followed streams every 2 minutes
      const interval = setInterval(fetchFollowedStreams, 2 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [user, session, fetchFollowedStreams]);

  // Monitor the in-play custom streams (yours or a shared list) with priority
  useEffect(() => {
    if (activeCustomStreams.length > 0) {
      Promise.all(activeCustomStreams.map(channel => chatMonitor.startMonitoring(channel, true)));
    }
  }, [activeCustomStreams]);

  const handleLogin = async () => {
    const url = await getAuthUrl();
    window.location.href = url;
  };

  const handleLogout = async () => {
    if (session) {
      await logout(session);
      setSession(null);
      setUser(null);
      setFollowedStreams([]);
      localStorage.removeItem('twitch_session');
      // Following has no meaning without a login — fall back to Top 100
      setStreamSource(prev => (prev === 'followed' ? 'top' : prev));
    }
  };

  // Stamp the monitor's latest reading onto a stream. Hands back the same
  // object while the numbers hold still, so memoised rows skip the tick.
  const updateStreamWithMetrics = useCallback((stream: StreamWithChatRate): StreamWithChatRate => {
    return withMetrics(stream, chatMonitor.getMetrics(stream.user_login), settingsRef.current.timeWindow);
  }, []);

  // The watched channel need not belong to any ranking or roster. Open its
  // embed immediately, then refresh live status even if it started offline.
  const selectedLogin = selectedStream?.user_login.toLowerCase() ?? null;
  useEffect(() => {
    if (!selectedLogin) return;
    let cancelled = false;
    const refresh = async () => {
      chatMonitor.startMonitoring(selectedLogin, true);
      try {
        const live = await getStreamByLogin(selectedLogin);
        if (cancelled) return;
        setSelectedStream(prev => {
          if (!prev || prev.user_login.toLowerCase() !== selectedLogin) return prev;
          if (!live) {
            const known = [...listStreamsRef.current, ...streamsRef.current]
              .find(s => s.user_login.toLowerCase() === selectedLogin && s.type === 'live');
            if (known) return updateStreamWithMetrics(known);
          }
          return updateStreamWithMetrics(live
            ? { ...live, chatRate: 0, messageCount: 0 }
            : { ...channelStream(selectedLogin), user_id: prev.user_id, user_name: prev.user_name, type: 'offline' });
        });
      } catch (err) {
        // Keep the player and last known metadata during API outages.
        console.error('Error refreshing watched channel:', err);
      }
    };
    refresh();
    getUsersByLogin([selectedLogin]).then(users => {
      if (cancelled) return;
      const profile = users.find(u => u.login.toLowerCase() === selectedLogin);
      if (!profile) return;
      setSelectedStream(prev => prev?.user_login.toLowerCase() === selectedLogin
        ? { ...prev, user_id: profile.id, user_name: profile.display_name }
        : prev);
    });
    const interval = setInterval(refresh, 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [selectedLogin, updateStreamWithMetrics]);

  // Resolve every channel in the custom list and the active crew via
  // Helix — real titles, games, viewers and live state for the sidebar and
  // the auto-switch pool, plus profiles so offline entries keep a face
  const fetchListStreams = useCallback(async () => {
    const source = activeStreamSourceFor();
    const crew = isCrewChannel(source, channelsRef.current)
      ? resolveCrewRoster(source, crewRostersRef.current, channelsRef.current)
      : [];
    const logins = Array.from(new Set([
      ...activeCustomStreamsFor().map(s => s.toLowerCase()),
      ...crew.map(s => s.toLowerCase()),
    ]));

    // Channels that left the list/crew lose priority so they stop hogging
    // the monitor's channel budget
    chatMonitor.demotePriorityExcept([
      ...logins,
      ...followedStreamsRef.current.map(s => s.user_login),
      ...(selectedStreamRef.current ? [selectedStreamRef.current.user_login] : []),
    ]);

    if (logins.length === 0) {
      listStreamsRef.current = [];
      setListStreams([]);
      return;
    }

    const [live, users] = await Promise.all([
      getStreamsByLogin(logins),
      getUsersByLogin(logins),
    ]);

    const withRates: StreamWithChatRate[] = live.map(s => ({ ...s, chatRate: 0, messageCount: 0 }));
    // Live list channels join the chat monitor as priority so their heat is real
    await Promise.all(withRates.map(s => chatMonitor.startMonitoring(s.user_login, true)));

    if (source !== activeStreamSourceFor()) return;
    listStreamsRef.current = withRates.map(updateStreamWithMetrics);
    setListStreams(listStreamsRef.current);
    if (users.length > 0) {
      setListUsers(prev => {
        const next = { ...prev };
        users.forEach(u => { next[u.login.toLowerCase()] = u; });
        return next;
      });
    }

    // When starting on a roster channel with no stream selected, tune to the top live streamer in this roster
    setSelectedStream(prev => {
      if (source !== activeStreamSourceFor()) return prev;
      if (source === 'npxl') {
        if (partyRef.current && !partyRef.current.isHost) return prev;
        const eligible = filterNpxlStreams(listStreamsRef.current, crew);
        // Keep explicit streamer links outside the roster; a roster member
        // who stops playing GTA leaves the rotation on the next refresh.
        if (prev && !crew.includes(prev.user_login.toLowerCase())) return prev;
        return eligible.find(s => s.user_login.toLowerCase() === prev?.user_login.toLowerCase())
          ?? eligible[0] ?? null;
      }
      if (!prev && withRates.length > 0) {
        return withRates[0];
      }
      if (!prev && logins.length > 0) {
        return channelStream(logins[0]);
      }
      return prev;
    });
  }, [updateStreamWithMetrics, activeCustomStreamsFor, activeStreamSourceFor]);

  useEffect(() => {
    if (sharedListLoading) return;
    fetchListStreams();
    // Live/offline state changes on Twitch's side — refresh every minute
    const interval = setInterval(fetchListStreams, 60 * 1000);
    return () => clearInterval(interval);
  }, [activeCustomStreams, streamSource, crewRosters, sharedListLoading, fetchListStreams]);

  const fetchStreams = useCallback(async () => {
    try {
      // Full-screen loader only before the first paint; later refreshes are silent
      // so the 5-minute stream list refresh never unmounts the player mid-watch.
      const isFirstLoad = streamsRef.current.length === 0;
      if (isFirstLoad) setLoading(true);
      setError(null);

      // Always the actual top 100 (of all Twitch, or of the armed category) —
      // rec/custom pools resolve separately via fetchListStreams, so the Top
      // tab never gets swapped out under you
      const topStreams = await getTopStreams(
        100,
        settingsRef.current.languageFilter,
        settingsRef.current.gameFilter?.id,
      );
      let fetchedStreams: StreamWithChatRate[] = topStreams.map(stream => ({
        ...stream,
        chatRate: 0,
        messageCount: 0,
      }));

      // The API can return the same stream twice across pages — dedupe by id
      // so React list keys stay unique
      const seenIds = new Set<string>();
      fetchedStreams = fetchedStreams.filter(stream => {
        if (seenIds.has(stream.id)) return false;
        seenIds.add(stream.id);
        return true;
      });

      // Sync the monitor's base pool: joins the new top set and parts channels
      // that fell out of it, so a category/language change never wedges
      // against the channel cap. Priority (followed/custom) channels survive.
      await chatMonitor.syncBaseChannels(fetchedStreams.map(stream => stream.user_login));

      // Carry current metrics into the fresh list so refreshes don't zero the ranking
      fetchedStreams = fetchedStreams.map(updateStreamWithMetrics);

      streamsRef.current = fetchedStreams;
      setStreams(fetchedStreams);

      // Select first stream if no stream is selected
      // A shared list link starts on that list's first channel; otherwise
      // respect the saved settings (followed starts with followed, etc.)
      setSelectedStream(prev => {
        if (!prev && fetchedStreams.length > 0) {
          const shared = sharedListRef.current;

          if (shared && shared.streams.length > 0) {
            const firstShared = shared.streams[0].toLowerCase();
            const known = [...fetchedStreams, ...listStreamsRef.current]
              .find(s => s.user_login.toLowerCase() === firstShared);
            if (known) return known;
            // Not resolved yet — placeholder keeps the player pointed right
            return {
              id: firstShared,
              user_id: '',
              user_login: firstShared,
              user_name: firstShared,
              game_id: '',
              game_name: '',
              type: 'live',
              title: 'Loading...',
              viewer_count: 0,
              started_at: '',
              language: '',
              thumbnail_url: '',
              tag_ids: [],
              tags: [],
              is_mature: false,
              chatRate: 0,
              messageCount: 0,
            } as StreamWithChatRate;
          }

          // For followed streams setting, select first followed
          if (settingsRef.current.streamSource === 'followed' && followedStreamsRef.current.length > 0) {
            return followedStreamsRef.current[0];
          }

          // For crew/topic channel rosters, wait for or use list streams
          if (isCrewChannel(settingsRef.current.streamSource, channelsRef.current)) {
            if (settingsRef.current.streamSource === 'npxl') {
              return filterNpxlStreams(listStreamsRef.current,
                resolveCrewRoster('npxl', crewRostersRef.current, channelsRef.current))[0] ?? null;
            }
            if (listStreamsRef.current.length > 0) {
              return listStreamsRef.current[0];
            }
            return null;
          }

          // Default to first top stream
          return fetchedStreams[0];
        }
        return prev;
      });

      setLoading(false);
    } catch (err) {
      console.error('Error fetching streams:', err);
      // Keep showing the last good list; only surface the error screen when we have nothing
      if (streamsRef.current.length === 0) {
        setError('Failed to fetch streams. Please check the backend server.');
      }
      setLoading(false);
    }
  }, [updateStreamWithMetrics]);

  // The pool auto-switch (and ←/→ stepping) draws from — the active channel.
  // Crews and custom merge every pool so roster channels outside the top 100
  // are still reachable — dedupeByLogin keeps one entry per channel.
  const getSourcePool = useCallback((
    topPool: StreamWithChatRate[],
    followedPool: StreamWithChatRate[],
    listPool: StreamWithChatRate[],
  ): StreamWithChatRate[] => {
    const source = activeStreamSourceFor();
    if (source === 'followed' && followedPool.length > 0) {
      return followedPool;
    }
    if (source === 'npxl') {
      // The minute-by-minute roster lookup owns live/category state; the
      // top pool can be five minutes old and still show a previous game.
      return filterNpxlStreams(listPool, resolveCrewRoster(source, crewRostersRef.current, channelsRef.current));
    }
    if (isCrewChannel(source, channelsRef.current)) {
      const crew = new Set(resolveCrewRoster(source, crewRostersRef.current, channelsRef.current));
      return dedupeByLogin([...topPool, ...followedPool, ...listPool])
        .filter(s => crew.has(s.user_login.toLowerCase()));
    }
    const customPool = activeCustomStreamsFor();
    if (source === 'custom' && customPool.length > 0) {
      const customSet = new Set(customPool.map(s => s.toLowerCase()));
      return dedupeByLogin([...topPool, ...followedPool, ...listPool])
        .filter(s => customSet.has(s.user_login.toLowerCase()));
    }
    return topPool;
  }, [activeStreamSourceFor, activeCustomStreamsFor]);

  // A spike while you're on another tab: one browser notification per
  // stream per five minutes, only if alerts are on and the browser allows it
  const alertIfHidden = useCallback((stream: StreamWithChatRate, switched: boolean) => {
    const now = Date.now();
    const allowed = shouldNotify({
      enabled: settingsRef.current.notifyWhenHidden,
      hidden: document.hidden,
      permission: notificationPermission(),
      now,
      lastAt: lastAlertRef.current.get(stream.id),
    });
    if (!allowed) return;
    lastAlertRef.current.set(stream.id, now);
    showSpikeNotification(spikeNotification(stream, getHeatRatio(stream), switched), () => {
      setSelectedStream(stream);
      broadcastSelectedStreamToParty(stream);
      setSuggestedStream(null);
      setPopupTimeLeft(0);
      if (popupTimerRef.current) {
        clearInterval(popupTimerRef.current);
        popupTimerRef.current = null;
      }
      lastSwitchTime.current = Date.now();
    });
  }, [broadcastSelectedStreamToParty]);

  const updateChatRates = useCallback(async () => {
    try {
      const now = Date.now();

      // Update cooldown progress
      const timeSinceLastSwitch = (now - lastSwitchTime.current) / 1000; // in seconds
      const progress = Math.min((timeSinceLastSwitch / settingsRef.current.cooldown) * 100, 100);
      setCooldownProgress(progress);

      // Also update followed streams with chat rate metrics
      if (followedStreamsRef.current.length > 0) {
        const updatedFollowed = followedStreamsRef.current.map(stream =>
          updateStreamWithMetrics(stream)
        );
        followedStreamsRef.current = updatedFollowed;
        setFollowedStreams(updatedFollowed);
      }

      // And the resolved custom/rec channels
      if (listStreamsRef.current.length > 0) {
        const updatedList = listStreamsRef.current.map(stream =>
          updateStreamWithMetrics(stream)
        );
        listStreamsRef.current = updatedList;
        setListStreams(updatedList);
      }

      const updatedStreams = streamsRef.current.map(stream =>
        updateStreamWithMetrics(stream)
      );

      // Sort all streams for sidebar display (no filters applied here)
      const sortedStreams = [...updatedStreams].sort((a, b) => {
        if (settingsRef.current.switchMode === 'threshold' && a.chatRate5m && b.chatRate5m && a.chatRate5m > 0 && b.chatRate5m > 0) {
          // Sort by heat ratio (10s / 5m average)
          const heatA = (a.chatRate10s || 0) / a.chatRate5m;
          const heatB = (b.chatRate10s || 0) / b.chatRate5m;
          return heatB - heatA;
        }
        // Default: sort by chat rate
        return b.chatRate - a.chatRate;
      });

      // The auto-switch pool: the active channel, through the viewer's filters
      const settings = settingsRef.current;
      const filteredForSwitch = filterSwitchPool(
        getSourcePool(updatedStreams, followedStreamsRef.current, listStreamsRef.current),
        settings,
      );

      // Publish the fresh sorted list before any switching side effects
      streamsRef.current = sortedStreams;
      setStreams(sortedStreams);

      // Watched-channel metadata refreshes independently; a stale ranking
      // must not turn an offline channel live again. Its chat still ticks.
      setSelectedStream(prev => prev ? updateStreamWithMetrics(prev) : prev);

      // Auto-switch logic based on mode with cooldown.
      // Guests follow the host's stream. Hosts can still auto-switch and sync the party.
      const autoSwitchAllowed = !partyRef.current || partyRef.current.isHost;
      if (!autoSwitchAllowed || timeSinceLastSwitch < settings.cooldown) {
        return;
      }

      const nextStream = pickSwitchTarget(filteredForSwitch, settings, {
        everything: activeStreamSourceFor() === 'npxl'
          ? getSourcePool(updatedStreams, followedStreamsRef.current, listStreamsRef.current)
          : [...updatedStreams, ...followedStreamsRef.current, ...listStreamsRef.current],
      });
      if (!nextStream) return;

      // Waved off recently — leave it alone
      const dismissed = dismissedStreamsRef.current;
      dismissed.sweep(now);
      if (dismissed.isDismissed(nextStream.id, now)) return;

      // Already watching it — nothing to do
      const current = selectedStreamRef.current;
      if (current && current.id === nextStream.id) return;

      if (settingsRef.current.autoSwitchBehavior === 'popup') {
        // Show popup instead of auto-switching. If it's already up for this
        // stream, let its countdown keep running instead of re-arming at 10s.
        if (suggestedStreamRef.current?.id === nextStream.id) return;

        setSuggestedStream(nextStream);
        suggestedStreamRef.current = nextStream;
        setPopupTimeLeft(10);
        if (popupTimerRef.current) {
          clearInterval(popupTimerRef.current);
        }
        popupTimerRef.current = setInterval(() => {
          setPopupTimeLeft(t => {
            if (t <= 1) {
              if (popupTimerRef.current) {
                clearInterval(popupTimerRef.current);
                popupTimerRef.current = null;
              }
              // Timed out unanswered — snooze it so it can't re-arm every ten seconds
              const expired = suggestedStreamRef.current;
              if (expired) dismissedStreamsRef.current.dismiss(expired.id, Date.now(), SNOOZE_MS);
              setSuggestedStream(null);
              return 0;
            }
            return t - 1;
          });
        }, 1000);
        alertIfHidden(nextStream, false);
        return; // cooldown resets only on an actual switch
      }

      lastSwitchTime.current = now;
      broadcastSelectedStreamToParty(nextStream);
      setSelectedStream(nextStream);
      alertIfHidden(nextStream, true);
    } catch (error) {
      console.error('Error updating chat rates:', error);
    }
  }, [updateStreamWithMetrics, broadcastSelectedStreamToParty, getSourcePool, alertIfHidden, activeStreamSourceFor]);

  useEffect(() => {
    // Wait for shared list to load first
    if (sharedListLoading) return;

    fetchStreams();

    // Update chat rates every 2 seconds
    const interval = setInterval(() => {
      updateChatRates();
    }, 2000);

    // Refresh stream list every 5 minutes
    const refreshInterval = setInterval(() => {
      fetchStreams();
    }, 5 * 60 * 1000);

    return () => {
      clearInterval(interval);
      clearInterval(refreshInterval);
      chatMonitor.stopAll();
    };
  }, [fetchStreams, updateChatRates, sharedListLoading]);

  // Refetch streams when the language filter or category lock changes
  useEffect(() => {
    if (sharedListLoading) return;
    fetchStreams();
  }, [languageFilter, gameFilter?.id, fetchStreams, sharedListLoading]);

  // Update chatMonitor spam filter when settings change
  useEffect(() => {
    chatMonitor.setSpamFilter(spamFilter);
  }, [spamFilter]);

  // Resolve a party stream against known streams, falling back to a placeholder
  const resolvePartyStream = useCallback((ps: PartyStream): StreamWithChatRate => {
    const login = ps.user_login.toLowerCase();
    const known = [...streamsRef.current, ...followedStreamsRef.current, ...listStreamsRef.current].find(
      s => s.user_login.toLowerCase() === login
    );
    if (known) return known;
    return {
      id: ps.id || login,
      user_id: '',
      user_login: login,
      user_name: ps.user_name || login,
      game_id: '',
      game_name: ps.game_name || '',
      type: 'live',
      title: ps.title || '',
      viewer_count: ps.viewer_count || 0,
      started_at: '',
      language: '',
      thumbnail_url: ps.thumbnail_url || '',
      tag_ids: [],
      tags: [],
      is_mature: false,
      chatRate: 0,
      messageCount: 0,
    } as StreamWithChatRate;
  }, []);

  const handleStreamSelect = useCallback((stream: StreamWithChatRate) => {
    setSelectedStream(stream);
    broadcastSelectedStreamToParty(stream);
  }, [broadcastSelectedStreamToParty]);

  const handleMobileStreamSelect = useCallback((stream: StreamWithChatRate) => {
    handleStreamSelect(stream);
    // Auto-hide sidebar on mobile after selecting a stream
    if (window.innerWidth <= 768) {
      setSidebarHidden(true);
    }
  }, [handleStreamSelect]);

  // Party: create
  const handleCreateParty = useCallback(async (nickname: string) => {
    setPartyBusy(true);
    setPartyError(null);
    try {
      const result = await createParty(nickname, selectedStream);
      setParty(result.party);
      setPartyParticipantId(result.participantId);
      setPartyIsHost(result.isHost);
      setShowPartyLauncher(false);
      setPendingPartyCode(null);
      setPartyPanelOpen(true);
      window.history.replaceState({}, '', `/party/${result.code}`);
    } catch (err: any) {
      setPartyError(err?.response?.data?.error || 'Failed to create party');
    } finally {
      setPartyBusy(false);
    }
  }, [selectedStream]);

  // Party: join
  const handleJoinParty = useCallback(async (code: string, nickname: string) => {
    setPartyBusy(true);
    setPartyError(null);
    try {
      const result = await joinParty(code, nickname);
      setParty(result.party);
      setPartyParticipantId(result.participantId);
      setPartyIsHost(result.isHost);
      setShowPartyLauncher(false);
      setPendingPartyCode(null);
      setPartyPanelOpen(true);
      window.history.replaceState({}, '', `/party/${result.code}`);
      // If party already has a stream, switch to it
      if (result.party.stream) {
        setSelectedStream(resolvePartyStream(result.party.stream));
      }
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setPartyError('Party not found');
      } else {
        setPartyError(err?.response?.data?.error || 'Failed to join party');
      }
    } finally {
      setPartyBusy(false);
    }
  }, [resolvePartyStream]);

  // Party: leave
  const handleLeaveParty = useCallback(() => {
    const current = partyRef.current;
    if (current) {
      leaveParty(current.code, current.participantId);
    }
    setParty(null);
    setPartyParticipantId(null);
    setPartyIsHost(false);
    setPartyPanelOpen(true);
    setPartyError(null);
    lastBroadcastStreamIdRef.current = null;
    if (window.location.pathname.startsWith('/party/')) {
      window.history.replaceState({}, '', '/');
    }
  }, []);

  // Party: open launcher button
  const handleOpenPartyButton = useCallback(() => {
    if (party) {
      setPartyPanelOpen(true);
    } else {
      setShowPartyLauncher(true);
    }
  }, [party]);

  // Party: SSE subscription + heartbeat
  useEffect(() => {
    const partyCode = party?.code;
    if (!partyCode || !partyParticipantId) return;

    const unsubscribe = subscribeParty(partyCode, {
      onState: (next) => {
        setParty(next);
        // Update host flag in case host changed
        const me = next.participants.find(p => p.id === partyParticipantId);
        if (me) setPartyIsHost(me.isHost);
      },
      onStream: (ps) => {
        setParty(prev => prev ? { ...prev, stream: ps } : prev);
        // Guests follow host. Hosts already switched locally when they selected.
        if (!partyRef.current?.isHost) {
          const resolved = resolvePartyStream(ps);
          setSelectedStream(resolved);
        }
      },
      onMessage: (msg) => {
        setParty(prev => {
          if (!prev) return prev;
          const messages = [...prev.messages, msg];
          if (messages.length > 200) messages.splice(0, messages.length - 200);
          return { ...prev, messages };
        });
      },
    });

    const heartbeat = setInterval(() => {
      partyHeartbeat(partyCode, partyParticipantId).catch(() => {});
    }, 20000);

    return () => {
      unsubscribe();
      clearInterval(heartbeat);
    };
  }, [party?.code, partyParticipantId, resolvePartyStream]);

  // Leave party on page unload
  useEffect(() => {
    const handler = () => {
      const current = partyRef.current;
      if (current && typeof navigator.sendBeacon === 'function') {
        const blob = new Blob([JSON.stringify({ participantId: current.participantId })], { type: 'application/json' });
        navigator.sendBeacon(
          `${API_BASE}/api/party/${encodeURIComponent(current.code)}/leave`,
          blob
        );
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // Shared list: take a copy home, or step back to your own rotation
  const leaveSharedList = useCallback(() => {
    setSharedList(null);
    if (parseListPath(window.location.pathname)) {
      window.history.replaceState({}, '', '/');
    }
  }, []);

  const adoptSharedList = useCallback(() => {
    const shared = sharedListRef.current;
    if (!shared) return;
    setCustomStreams(shared.streams.slice(0, MAX_CUSTOM_STREAMS));
    setStreamSource('custom');
    leaveSharedList();
  }, [leaveSharedList]);

  const handleBlacklistCurrent = () => {
    if (selectedStream && !streamBlacklist.includes(selectedStream.user_login.toLowerCase())) {
      setStreamBlacklist([...streamBlacklist, selectedStream.user_login.toLowerCase()]);
    }
  };

  // Confirm switch from popup
  const confirmSuggestedSwitch = useCallback(() => {
    if (suggestedStream) {
      setSelectedStream(suggestedStream);
      broadcastSelectedStreamToParty(suggestedStream);
      setSuggestedStream(null);
      setPopupTimeLeft(0);
      lastSwitchTime.current = Date.now(); // Reset cooldown on actual switch
      if (popupTimerRef.current) {
        clearInterval(popupTimerRef.current);
        popupTimerRef.current = null;
      }
    }
  }, [suggestedStream, broadcastSelectedStreamToParty]);

  // Dismiss popup
  const dismissSuggestedSwitch = useCallback(() => {
    if (suggestedStream) {
      dismissedStreamsRef.current.dismiss(suggestedStream.id, Date.now(), DISMISS_MS);
    }
    setSuggestedStream(null);
    setPopupTimeLeft(0);
    if (popupTimerRef.current) {
      clearInterval(popupTimerRef.current);
      popupTimerRef.current = null;
    }
  }, [suggestedStream]);

  // Step through the ranked list with the keyboard — inside the stream
  // source you picked, so custom/rec users cycle their own rotation
  const navigateStream = useCallback((dir: 1 | -1) => {
    const list = getSourcePool(streamsRef.current, followedStreamsRef.current, listStreamsRef.current);
    if (list.length === 0) return;
    const currentId = selectedStreamRef.current?.id;
    const idx = list.findIndex(s => s.id === currentId);
    const nextIdx = idx === -1 ? 0 : (idx + dir + list.length) % list.length;
    const stream = list[nextIdx];
    if (stream) {
      setSelectedStream(stream);
      broadcastSelectedStreamToParty(stream);
    }
  }, [broadcastSelectedStreamToParty, getSourcePool]);

  // Jump to the configured favorite stream if it's live somewhere in our pools
  const jumpToFavorite = useCallback(() => {
    const fav = settingsRef.current.favoriteStream.toLowerCase();
    if (!fav) return;
    const pool = activeStreamSourceFor() === 'npxl'
      ? getSourcePool(streamsRef.current, followedStreamsRef.current, listStreamsRef.current)
      : [...streamsRef.current, ...followedStreamsRef.current, ...listStreamsRef.current];
    const stream = pool.find(
      s => s.user_login.toLowerCase() === fav
    );
    if (stream) {
      setSelectedStream(stream);
      broadcastSelectedStreamToParty(stream);
    }
  }, [broadcastSelectedStreamToParty, activeStreamSourceFor, getSourcePool]);

  // Tuning to a channel moves the player inside it: if what's playing isn't
  // in the new pool, jump to that pool's loudest chat. Rosters can still be
  // resolving right after the switch, so keep trying for a few seconds.
  const pendingTuneRef = useRef<number | null>(null);
  const firstSourceRef = useRef(true);
  useEffect(() => {
    if (firstSourceRef.current) {
      firstSourceRef.current = false; // saved source on load — never yank the opening stream
      return;
    }
    pendingTuneRef.current = Date.now();
  }, [streamSource]);

  useEffect(() => {
    const since = pendingTuneRef.current;
    if (since === null) return;
    if (Date.now() - since > 10000) {
      pendingTuneRef.current = null;
      return;
    }
    // Guests follow the host whatever channel they browse
    if (partyRef.current && !partyRef.current.isHost) {
      pendingTuneRef.current = null;
      return;
    }
    const pool = getSourcePool(streamsRef.current, followedStreamsRef.current, listStreamsRef.current);
    if (pool.length === 0) {
      if (streamSource === 'npxl') setSelectedStream(null);
      return;
    }
    pendingTuneRef.current = null;
    const current = selectedStreamRef.current;
    if (current && pool.some(s => s.id === current.id)) return;
    const loudest = [...pool].sort((a, b) => (b.chatRate10s || b.chatRate) - (a.chatRate10s || a.chatRate))[0];
    setSelectedStream(loudest);
    broadcastSelectedStreamToParty(loudest);
  }, [streamSource, streams, listStreams, followedStreams, getSourcePool, broadcastSelectedStreamToParty]);

  // Hotkeys: S/D confirm or dismiss the popup, ←/→ step the ranked list,
  // F jumps to the favorite, B blacklists the current stream, G opens the guide
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only trigger if not typing in an input
      const target = e.target as HTMLElement;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        target?.isContentEditable
      ) {
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const key = e.key.toLowerCase();
      if (key === 's' && suggestedStream) {
        e.preventDefault();
        confirmSuggestedSwitch();
      } else if (key === 'd' && suggestedStream) {
        e.preventDefault();
        dismissSuggestedSwitch();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        navigateStream(1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        navigateStream(-1);
      } else if (key === 'f') {
        e.preventDefault();
        jumpToFavorite();
      } else if (key === 'b') {
        e.preventDefault();
        const login = selectedStreamRef.current?.user_login.toLowerCase();
        if (login) {
          setStreamBlacklist(prev => (prev.includes(login) ? prev : [...prev, login]));
        }
      } else if (key === 'g') {
        e.preventDefault();
        setShowGuide(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [suggestedStream, confirmSuggestedSwitch, dismissSuggestedSwitch, navigateStream, jumpToFavorite]);

  // The tab title carries the watched channel and, once it's warm, its heat
  const titleName = selectedStream?.user_name ?? null;
  const titleHeat = selectedStream && isHeatReady(selectedStream)
    ? Math.round(getHeatRatio(selectedStream) * 10) / 10
    : 0;
  const titleTier = getHeatTier(titleHeat);
  useEffect(() => {
    document.title = formatPageTitle(titleName ? { name: titleName, heat: titleHeat, tier: titleTier } : null);
  }, [titleName, titleHeat, titleTier]);

  if (loading && !selectedStream) {
    return (
      <div className="app-loading">
        <div className="loading-radar">
          <span className="radar-ring" />
          <span className="radar-ring r2" />
          <img src="/pogchamp.png" alt="" className="loading-emote" />
        </div>
        <p className="loading-label">tuning in to the top 100 chats</p>
      </div>
    );
  }

  if (error && !selectedStream) {
    return (
      <div className="app-error">
        <h2>Connection Error</h2>
        <p>{error}</p>
        <button onClick={fetchStreams}>Retry</button>
      </div>
    );
  }

  const dismissGuide = () => {
    setShowGuide(false);
    localStorage.setItem('guideShown', 'true');
  };

  // Which room is docked under the Twitch chat: the active channel's — a
  // shared list is someone's Custom (no room), and a running watch party
  // takes the slot instead
  const activeChannel = getChannel(sharedList ? 'custom' : streamSource, channels);
  const channelPanel = !party && activeChannel.room ? (
    <ChannelChat
      room={activeChannel.room}
      label={activeChannel.label}
      preferredNickname={user?.display_name || user?.login}
      collapsed={channelChatCollapsed}
      onToggleCollapsed={toggleChannelChat}
    />
  ) : undefined;

  // Check if on /stats page
  if (window.location.pathname === '/stats') {
    return <StatsPanel />;
  }

  // The watched chat's last five minutes, for the console's seismograph
  const selectedHistory = selectedStream ? chatMonitor.getHistory(selectedStream.user_login) : [];

  return (
    <div className="app-container">
      {showGuide && (
        <div className="guide-overlay" onClick={dismissGuide}>
          <div className="guide-popup" onClick={e => e.stopPropagation()} role="dialog" aria-label="How pogchamp.tv works">
            <div className="guide-head">
              <img src="/pogchamp.png" alt="" className="guide-emote" />
              <div>
                <h2>pogchamp<span className="brand-tld">.tv</span></h2>
                <p className="guide-lede">Auto-switch to the loudest chat on Twitch.</p>
              </div>
            </div>

            {/* Three beats of the actual pipeline — each marker is the real
                parameter it runs on, not an ordinal. */}
            <ol className="guide-beats">
              <li>
                <span className="beat-mark">{streams.length || 100} chats</span>
                <div className="beat-body">
                  <h3>Listen</h3>
                  <p>One anonymous connection counts every message in the top streams - plus anyone you follow or add yourself.</p>
                </div>
              </li>
              <li>
                <span className="beat-mark">10s / 5m</span>
                <div className="beat-body">
                  <h3>Measure</h3>
                  <p>Heat is the last 10 seconds of chat against that channel's 5-minute average. Three times the usual traffic means something just happened.</p>
                </div>
              </li>
              <li>
                <span className="beat-mark">{threshold}×</span>
                <div className="beat-body">
                  <h3>Jump</h3>
                  <p>Clear your threshold and the player moves there. Set it, or switch to Popup mode and confirm each jump with <kbd>S</kbd>.</p>
                </div>
              </li>
            </ol>

            {/* The colour language used everywhere else in the app */}
            <div className="guide-ramp">
              <span className="ramp-title">Heat</span>
              <div className="ramp-bar" aria-hidden />
              <div className="ramp-marks">
                <span className="heat-cold">quiet</span>
                <span className="heat-warm">2× busy</span>
                <span className="heat-hot">3× hot</span>
                <span className="heat-blazing">5× blazing</span>
              </div>
            </div>

            <div className="guide-keys">
              <span className="keys-title">Keys</span>
              <div className="keys-grid">
                <span><kbd>S</kbd><kbd>D</kbd> take / skip a switch</span>
                <span><kbd>←</kbd><kbd>→</kbd> step the list</span>
                <span><kbd>F</kbd> favourite stream</span>
                <span><kbd>B</kbd> blacklist this one</span>
                <span><kbd>M</kbd> mute</span>
                <span><kbd>G</kbd> reopen this</span>
              </div>
            </div>

            <details className="guide-more">
              <summary>Everything in Settings</summary>
              <ul>
                <li><strong>Auto-switch</strong> - Highest jumps to the busiest chat, Threshold waits for a spike, Random keeps you honest.</li>
                <li><strong>Channels</strong> - the dial at the top of the list: Top 100, the crews (Rec, OTK, FaZe, Ninja, AMP), the Just topics (Gaming, Politics, IRL, RP), the channels you follow, or your own custom list. Switching only ever happens inside the channel you pick.</li>
                <li><strong>Channel chat</strong> - every public channel has one shared room under the Twitch chat. Everyone tuned to Rec talks in Rec chat while each player follows the heat - a watch party with the engine as host.</li>
                <li><strong>Category lock</strong> - point the Top tab at a single game and everything (ranking, heat, auto-switch) runs inside that game's top 100. The URL becomes a shareable /g/ link.</li>
                <li><strong>Share your list</strong> - one click turns your custom list into a short link anyone can open. No account needed; opening someone's link never touches your own list.</li>
                <li><strong>Favourite</strong> - a home base the player returns to when nothing is spiking.</li>
                <li><strong>Cooldown</strong> - the shortest gap between two auto-switches.</li>
                <li><strong>Min chat rate</strong> - ignore channels below this many messages per second, however hard they spike.</li>
                <li><strong>Language, game &amp; stream blacklists</strong> - keep specific streams and categories out of the rotation.</li>
                <li><strong>Watch parties</strong> - share a code and everyone follows the host's switches, with a private chat of your own.</li>
                <li><strong>Alerts</strong> - switch on "When tab is hidden" and a browser notification tells you when a spike hits while you're somewhere else.</li>
                <li><strong>Ads</strong> - Twitch Turbo makes auto-switching seamless; mid-rolls interrupt a jump otherwise.</li>
              </ul>
            </details>

            <div className="guide-actions">
              <button className="guide-close-btn" onClick={dismissGuide}>Start watching</button>
              <a href="https://discord.gg/CSWHkcH8Ee" target="_blank" rel="noopener noreferrer" className="guide-social-btn discord" title="Join the Discord">
                <IconDiscord size={17} />
              </a>
              <a href="https://x.com/not_pr0" target="_blank" rel="noopener noreferrer" className="guide-social-btn twitter" title="@not_pr0 on X">
                <IconX size={14} />
              </a>
            </div>
          </div>
        </div>
      )}
      <Header
        onToggleSidebar={() => setSidebarHidden(!sidebarHidden)}
        sidebarVisible={!sidebarHidden}
        sharedLabel={sharedList
          ? (sharedList.label.startsWith('@') ? `${sharedList.label}'s list` : 'a shared list')
          : null}
        chatConnected={chatConnected}
        streams={!sharedList && streamSource === 'npxl'
          ? filterNpxlStreams(listStreams, resolveCrewRoster(streamSource, crewRosters, channels))
          : streams}
        selectedId={selectedStream?.id ?? null}
        onStreamSelect={handleStreamSelect}
      />
      <div className="app">
        {!sidebarHidden && (
          <>
            <div className="mobile-overlay" onClick={() => setSidebarHidden(true)} />
            <StreamList
              streams={streams}
              followedStreams={followedStreams}
              customStreams={activeCustomStreams}
              listStreams={listStreams}
              listUsers={listUsers}
              streamSource={streamSource}
              onStreamSourceChange={setStreamSource}
              channels={channels}
              channelAdmin={channelAdmin}
              recRoster={isCrewChannel(streamSource, channels) ? resolveCrewRoster(streamSource, crewRosters, channels) : undefined}
              onCustomStreamsChange={sharedList ? undefined : setCustomStreams}
              sharedLabel={sharedList?.label ?? null}
              onAdoptSharedList={adoptSharedList}
              onLeaveSharedList={leaveSharedList}
              gameFilter={gameFilter}
              onGameFilterChange={setGameFilter}
              selectedStream={selectedStream}
              onStreamSelect={handleMobileStreamSelect}
              onToggleSidebar={() => setSidebarHidden(true)}
              user={user}
              liveCount={liveCount}
              onLogin={handleLogin}
              onLogout={handleLogout}
              onShowGuide={() => setShowGuide(true)}
            />
          </>
        )}
        <StreamPlayer
          stream={selectedStream}
          history={selectedHistory}
          timeWindow={timeWindow}
          switchMode={switchMode}
          threshold={threshold}
          languageFilter={languageFilter}
          cooldown={cooldown}
          cooldownProgress={cooldownProgress}
          minChatRate={minChatRate}
          gameBlacklist={gameBlacklist}
          streamBlacklist={streamBlacklist}
          favoriteStream={favoriteStream}
          streamSource={streamSource}
          customStreams={customStreams}
          autoSwitchBehavior={autoSwitchBehavior}
          spamFilter={spamFilter}
          gameFilter={gameFilter}
          notifyWhenHidden={notifyWhenHidden}
          isLoggedIn={!!user}
          sessionId={session}
          sidebarHidden={sidebarHidden}
          chatConnected={chatConnected}
          partyActive={!!party}
          onOpenParty={handleOpenPartyButton}
          channelPanel={channelPanel}
          channelPanelCollapsed={channelChatCollapsed}
          partyPanel={party && partyParticipantId && partyPanelOpen ? (
            <WatchParty
              party={party}
              participantId={partyParticipantId}
              isHost={partyIsHost}
              onLeave={handleLeaveParty}
              onClose={() => setPartyPanelOpen(false)}
            />
          ) : undefined}
          onTimeWindowChange={setTimeWindow}
          onSwitchModeChange={setSwitchMode}
          onThresholdChange={setThreshold}
          onLanguageFilterChange={setLanguageFilter}
          onCooldownChange={setCooldown}
          onMinChatRateChange={setMinChatRate}
          onGameBlacklistChange={setGameBlacklist}
          onStreamBlacklistChange={setStreamBlacklist}
          onFavoriteStreamChange={setFavoriteStream}
          onCustomStreamsChange={setCustomStreams}
          onAutoSwitchBehaviorChange={setAutoSwitchBehavior}
          onSpamFilterChange={setSpamFilter}
          onNotifyWhenHiddenChange={setNotifyWhenHidden}
          onBlacklistCurrent={handleBlacklistCurrent}
          onToggleSidebar={() => setSidebarHidden(!sidebarHidden)}
        />
        {showPartyLauncher && (
          <WatchPartyLauncher
            onCreate={handleCreateParty}
            onJoin={handleJoinParty}
            onClose={() => {
              setShowPartyLauncher(false);
              setPartyError(null);
              setPendingPartyCode(null);
              if (window.location.pathname.startsWith('/party/')) {
                window.history.replaceState({}, '', '/');
              }
            }}
            defaultNickname={user?.display_name || user?.login}
            defaultCode={pendingPartyCode || undefined}
            busy={partyBusy}
            error={partyError}
          />
        )}
        {/* Switch suggestion popup - inside .app for fullscreen support */}
        {suggestedStream && (() => {
          const popupHeat = getHeatRatio(suggestedStream);
          const popupTier = getHeatTier(popupHeat);
          const popupKicker =
            switchMode === 'threshold' ? 'Heat spike'
            : switchMode === 'highest' ? 'Chat leader'
            : 'Up next';
          return (
          <div className="switch-popup">
            <div className="switch-popup-content">
              <img
                src={suggestedStream.thumbnail_url?.replace('{width}', '440').replace('{height}', '248') || ''}
                alt=""
                className="switch-popup-bg"
              />
              <div className="switch-popup-overlay">
                <div className="switch-popup-header">
                  <IconFlame size={13} className={`switch-popup-flame heat-${popupTier}`} />
                  <span className="switch-popup-kicker">{popupKicker}</span>
                  <span className="switch-popup-timer">{popupTimeLeft}s</span>
                </div>
                <div className="switch-popup-name">{suggestedStream.user_name}</div>
                {suggestedStream.game_name && (
                  <div className="switch-popup-game">{suggestedStream.game_name}</div>
                )}
                <div className="switch-popup-stats">
                  <span className="chip" title="Chat messages per second right now">
                    <IconBolt size={10} />
                    {suggestedStream.chatRate?.toFixed(1)}/s
                  </span>
                  {isHeatReady(suggestedStream) && (
                    <span className={`chip heat heat-${popupTier}`} title="Heat: last 10s vs the 5 minute average">
                      <IconFlame size={10} />
                      {popupHeat.toFixed(1)}x
                    </span>
                  )}
                </div>
                <div className="switch-popup-actions">
                  <button className="switch-popup-btn switch-popup-confirm" onClick={confirmSuggestedSwitch}>
                    Switch <kbd>S</kbd>
                  </button>
                  <button className="switch-popup-btn switch-popup-dismiss" onClick={dismissSuggestedSwitch}>
                    Dismiss <kbd>D</kbd>
                  </button>
                </div>
              </div>
            </div>
          </div>
          );
        })()}
      </div>
    </div>
  );
};

export default App;
